import React, { useState, useEffect, useCallback, useContext } from "react";
import { Button, Input, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Header from "../../layout/Header/Header";
import { posGet, posPost, posPatch, avisaError, avisaOk, hoy, sumaDias } from "./posApi";
import { AuthContext } from "../../contexts/AuthContext";
import SinPermiso from "./SinPermiso";

const VACIA = () => ({
  nombre: "", tipo: "NxM", lleva: 2, paga: 1, porcentaje: 10,
  alcance: "todo", articulos: [], lineas: [], marcas: [], colecciones: [], familias: [],
  areas: [], inicio: hoy(), fin: sumaDias(hoy(), 30), prioridad: 0, is_active: "Si",
});

// Lista de casillas para elegir varios elementos de un catálogo
const Checklist = ({ opciones, seleccion, onChange, etiqueta = (o) => o.name, alto = 180 }) => {
  const [filtro, setFiltro] = useState("");
  const vis = opciones.filter((o) => etiqueta(o).toLowerCase().includes(filtro.toLowerCase()));
  const toggle = (id) => onChange(seleccion.includes(id) ? seleccion.filter((x) => x !== id) : [...seleccion, id]);
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 8 }}>
      {opciones.length > 8 && <Input bsSize="sm" className="mb-2" placeholder="Filtrar…" value={filtro} onChange={(e) => setFiltro(e.target.value)} />}
      <div style={{ maxHeight: alto, overflowY: "auto" }}>
        {vis.map((o) => (
          <label key={o._id} style={{ display: "flex", gap: 8, alignItems: "center", margin: 0, padding: "3px 2px", cursor: "pointer", fontWeight: 400 }}>
            <input type="checkbox" checked={seleccion.includes(o._id)} onChange={() => toggle(o._id)} />{etiqueta(o)}
          </label>
        ))}
        {!vis.length && <div className="text-muted small">Sin opciones</div>}
      </div>
      <div className="small text-muted mt-1">{seleccion.length} seleccionado(s)</div>
    </div>
  );
};

const describe = (p) => (p.tipo === "NxM" ? `${p.lleva}x${p.paga}` : `${p.porcentaje}% desc.`);
const aplicaA = (p) => {
  const partes = [];
  if (p.lineas.length) partes.push("Líneas: " + p.lineas.map((x) => x.name).join(", "));
  if (p.marcas.length) partes.push("Marcas: " + p.marcas.map((x) => x.name).join(", "));
  if (p.colecciones.length) partes.push("Colecciones: " + p.colecciones.map((x) => x.name).join(", "));
  if (p.familias.length) partes.push("Familias: " + p.familias.map((x) => x.name).join(", "));
  if (p.articulos.length) partes.push("Modelos: " + p.articulos.map((x) => x.codigo).join(", "));
  return partes.length ? partes.join(" · ") : "Toda la mercancía";
};

function PosPromociones() {
  const { user } = useContext(AuthContext);
  const editar = !!(user && user.rolTiendas === "master" && user.posPromociones);
  const [promos, setPromos] = useState([]);
  const [cat, setCat] = useState({ lineas: [], marcas: [], colecciones: [], familias: [], tiendas: [] });
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busqArt, setBusqArt] = useState("");
  const [artsEncontrados, setArtsEncontrados] = useState([]);
  const [artsElegidos, setArtsElegidos] = useState([]); // objetos {_id,codigo,nombre}
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(() => { posGet("/promociones").then(setPromos).catch(avisaError); }, []);
  useEffect(() => {
    cargar();
    posGet("/catalogos").then(setCat).catch(avisaError);
  }, [cargar]);

  const abrir = (p) => {
    if (!p) { setForm(VACIA()); setEditId(null); setArtsElegidos([]); return; }
    const ids = (arr) => arr.map((x) => x._id);
    const conFiltro = ["articulos", "lineas", "marcas", "colecciones", "familias"].some((k) => p[k].length);
    setForm({
      ...VACIA(), ...p, alcance: conFiltro ? "seleccion" : "todo",
      articulos: ids(p.articulos), lineas: ids(p.lineas), marcas: ids(p.marcas),
      colecciones: ids(p.colecciones), familias: ids(p.familias), areas: ids(p.areas),
      lleva: p.lleva || 2, paga: p.paga || 1, porcentaje: p.porcentaje || 10,
    });
    setArtsElegidos(p.articulos);
    setEditId(p._id);
  };

  const buscarArticulos = async (e) => {
    e.preventDefault();
    try { setArtsEncontrados(await posGet("/articulosCatalogo", { q: busqArt })); } catch (er) { avisaError(er); }
  };
  const agregaArt = (a) => {
    if (form.articulos.includes(a._id)) return;
    setForm({ ...form, articulos: [...form.articulos, a._id] });
    setArtsElegidos([...artsElegidos, a]);
  };
  const quitaArt = (id) => {
    setForm({ ...form, articulos: form.articulos.filter((x) => x !== id) });
    setArtsElegidos(artsElegidos.filter((a) => a._id !== id));
  };

  const guardar = async () => {
    const datos = { ...form };
    if (datos.alcance === "todo") ["articulos", "lineas", "marcas", "colecciones", "familias"].forEach((k) => { datos[k] = []; });
    delete datos.alcance;
    setGuardando(true);
    try {
      if (editId) await posPatch(`/promociones/${editId}`, datos);
      else await posPost("/promociones", datos);
      avisaOk("Promoción guardada");
      setForm(null);
      cargar();
    } catch (e) { avisaError(e); } finally { setGuardando(false); }
  };

  const activa = (p) => p.is_active === "Si" && p.inicio <= hoy() && p.fin >= hoy();
  const alternar = async (p) => {
    try {
      await posPatch(`/promociones/${p._id}`, {
        ...p, is_active: p.is_active === "Si" ? "No" : "Si",
        articulos: p.articulos.map((x) => x._id), lineas: p.lineas.map((x) => x._id), marcas: p.marcas.map((x) => x._id),
        colecciones: p.colecciones.map((x) => x._id), familias: p.familias.map((x) => x._id), areas: p.areas.map((x) => x._id),
      });
      cargar();
    } catch (e) { avisaError(e); }
  };

  const f = form;
  if (!(user && user.rolTiendas && user.posPromociones)) return <SinPermiso que="ver promociones" />;

  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-tags mr-2" style={{ color: "#f59e0b" }} />Promociones</h3>
            <p className="page-subtitle">2x1, 3x2, 3x1 (se cobran las más caras) o % de descuento · por tienda o generales · se aplican solas en la caja</p>
          </div>
          <div className="page-actions">
            {editar && <Button color="success" onClick={() => abrir(null)}><i className="fas fa-plus mr-1" />Nueva promoción</Button>}
            <Button color="secondary" size="sm" href="/PuntoVenta"><i className="fas fa-th-large mr-1" />Menú</Button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-sm table-dark-header">
            <thead><tr><th>Promoción</th><th>Tipo</th><th>Aplica a</th><th>Tiendas</th><th>Vigencia</th><th>Estado</th><th /></tr></thead>
            <tbody>
              {!promos.length && <tr><td colSpan={7} className="text-center text-muted py-4">Aún no hay promociones</td></tr>}
              {promos.map((p) => (
                <tr key={p._id} style={p.is_active === "No" ? { color: "#94a3b8" } : {}}>
                  <td><strong>{p.nombre}</strong></td>
                  <td><span className="badge badge-warning">{describe(p)}</span></td>
                  <td style={{ maxWidth: 320 }}>{aplicaA(p)}</td>
                  <td>{p.areas.length ? p.areas.map((a) => a.name).join(", ") : "Todas"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{p.inicio} al {p.fin}</td>
                  <td>
                    {p.is_active === "No" ? <span className="badge badge-secondary">Pausada</span>
                      : activa(p) ? <span className="badge badge-success">Vigente</span>
                      : p.inicio > hoy() ? <span className="badge badge-info">Programada</span>
                      : <span className="badge badge-secondary">Vencida</span>}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {editar && (<>
                    <Button size="sm" color="info" onClick={() => abrir(p)} title="Editar"><i className="fas fa-pen" /></Button>{" "}
                    <Button size="sm" color={p.is_active === "Si" ? "secondary" : "success"} onClick={() => alternar(p)} title={p.is_active === "Si" ? "Pausar" : "Reactivar"}>
                      <i className={`fas fa-${p.is_active === "Si" ? "pause" : "play"}`} />
                    </Button>
                    </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={!!f} toggle={() => setForm(null)} size="lg">
        <ModalHeader toggle={() => setForm(null)}>{editId ? "Editar promoción" : "Nueva promoción"}</ModalHeader>
        {f && (
          <ModalBody>
            <div className="row">
              <div className="col-md-8 mb-3"><label>Nombre (sale en el ticket)</label><Input value={f.nombre} placeholder="Ej. 2x1 en blusas" onChange={(e) => setForm({ ...f, nombre: e.target.value })} /></div>
              <div className="col-md-4 mb-3">
                <label>Tipo</label>
                <Input type="select" value={f.tipo} onChange={(e) => setForm({ ...f, tipo: e.target.value })}>
                  <option value="NxM">Lleva N, paga M (2x1, 3x2…)</option>
                  <option value="descuento">% de descuento</option>
                </Input>
              </div>
              {f.tipo === "NxM" ? (
                <>
                  <div className="col-6 col-md-3 mb-3"><label>Lleva</label><Input type="number" min="2" value={f.lleva} onChange={(e) => setForm({ ...f, lleva: e.target.value })} /></div>
                  <div className="col-6 col-md-3 mb-3"><label>Paga</label><Input type="number" min="1" value={f.paga} onChange={(e) => setForm({ ...f, paga: e.target.value })} /></div>
                  <div className="col-md-6 mb-3 small text-muted" style={{ alignSelf: "flex-end" }}>
                    Se cobran las {f.paga} pieza(s) más caras de cada {f.lleva}; se pueden mezclar modelos dentro de la promoción.
                  </div>
                </>
              ) : (
                <div className="col-md-3 mb-3"><label>Descuento %</label><Input type="number" min="1" max="100" value={f.porcentaje} onChange={(e) => setForm({ ...f, porcentaje: e.target.value })} /></div>
              )}
              <div className="col-6 col-md-3 mb-3"><label>Desde</label><Input type="date" value={f.inicio} onChange={(e) => setForm({ ...f, inicio: e.target.value })} /></div>
              <div className="col-6 col-md-3 mb-3"><label>Hasta</label><Input type="date" value={f.fin} onChange={(e) => setForm({ ...f, fin: e.target.value })} /></div>
              <div className="col-md-3 mb-3"><label title="Si dos promociones 2x1/3x2 compiten por la misma pieza, gana la de mayor prioridad">Prioridad</label><Input type="number" value={f.prioridad} onChange={(e) => setForm({ ...f, prioridad: e.target.value })} /></div>
            </div>

            <label className="d-block">¿A qué mercancía aplica?</label>
            <div className="mb-2">
              <label className="mr-3" style={{ fontWeight: 400 }}><input type="radio" checked={f.alcance === "todo"} onChange={() => setForm({ ...f, alcance: "todo" })} /> Toda la mercancía</label>
              <label style={{ fontWeight: 400 }}><input type="radio" checked={f.alcance === "seleccion"} onChange={() => setForm({ ...f, alcance: "seleccion" })} /> Solo lo que elija</label>
            </div>
            {f.alcance === "seleccion" && (
              <div className="row">
                {[["lineas", "Líneas"], ["marcas", "Marcas"], ["colecciones", "Colecciones"], ["familias", "Familias"]].map(([k, t]) => (
                  cat[k].length ? (
                    <div key={k} className="col-md-6 mb-3"><label>{t}</label><Checklist opciones={cat[k]} seleccion={f[k]} onChange={(v) => setForm({ ...f, [k]: v })} alto={140} /></div>
                  ) : null
                ))}
                <div className="col-12 mb-3">
                  <label>Modelos específicos</label>
                  <form onSubmit={buscarArticulos} style={{ display: "flex", gap: 6 }}>
                    <Input bsSize="sm" placeholder="Buscar por código o nombre" value={busqArt} onChange={(e) => setBusqArt(e.target.value)} />
                    <Button size="sm" color="primary" type="submit">Buscar</Button>
                  </form>
                  {artsEncontrados.length > 0 && (
                    <div style={{ maxHeight: 140, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8, marginTop: 6 }}>
                      {artsEncontrados.map((a) => (
                        <div key={a._id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", borderBottom: "1px solid #f1f5f9" }}>
                          <span>{a.codigo} · {a.nombre}</span>
                          <Button size="sm" color="success" disabled={f.articulos.includes(a._id)} onClick={() => agregaArt(a)}>+</Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-2">
                    {artsElegidos.map((a) => (
                      <span key={a._id} className="badge badge-info mr-1 mb-1" style={{ fontSize: "0.8rem" }}>
                        {a.codigo} <i className="fas fa-times ml-1" style={{ cursor: "pointer" }} onClick={() => quitaArt(a._id)} />
                      </span>
                    ))}
                  </div>
                  <div className="small text-muted">Aplica si el modelo cumple cualquiera de los filtros elegidos.</div>
                </div>
              </div>
            )}

            <label className="d-block mt-2">¿En qué tiendas?</label>
            <div className="small text-muted mb-1">Si no eliges ninguna, aplica en todas las tiendas.</div>
            <Checklist opciones={cat.tiendas} seleccion={f.areas} onChange={(v) => setForm({ ...f, areas: v })} etiqueta={(t) => `${t.clave ? t.clave + " · " : ""}${t.name}`} />
          </ModalBody>
        )}
        <ModalFooter>
          <Button color="secondary" onClick={() => setForm(null)}>Cancelar</Button>
          <Button color="success" disabled={guardando} onClick={guardar}>{guardando ? <i className="fas fa-spinner fa-spin" /> : "Guardar"}</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default PosPromociones;
